import type { ICartProduct } from '@/types';

import { cartPriceUpdateService } from '@/lib/services/cart-price-update.service';
import { cartStockUpdateService } from '@/lib/services/cart-stock-update.service';

/**
 * After LWW merge, align cart lines with Typesense (prices) and stock rules used at checkout.
 * PBM lines skip price refresh; stock service already skips PBM for quantity checks.
 */
export async function enrichMergedCartWithFreshPricesAndStock(
  items: ICartProduct[],
): Promise<ICartProduct[]> {
  if (items.length === 0) return items;

  let working = items.map((item) => ({ ...item }));

  const nonPbm = working.filter((i) => !i?.pbm_info?.flag);
  if (nonPbm.length > 0) {
    const priceRes = await cartPriceUpdateService.updatePrices(
      nonPbm.map((item) => ({
        sku: item.sku || item.id,
        id: item.id,
        price: item.price,
        sale_price: item.sale_price ?? 0,
        promotional_pack_unit_price: item.promotional_pack_unit_price,
        promotional_pack_quantity: item.promotional_pack_quantity,
        product_prices: item.product_prices,
        quantity: item.quantity,
        stock: item.stock,
      })),
      { forceUpdate: true, silent: true },
    );

    if (priceRes.success && priceRes.updatedItems.length > 0) {
      const byId = new Map(priceRes.updatedItems.map((u) => [u.id, u]));
      working = working.map((item) => {
        if (item.pbm_info?.flag) return item;
        const u = byId.get(item.id);
        if (!u) return item;
        return {
          ...item,
          price: u.price,
          sale_price: u.sale_price,
          calculated_sale_price: u.sale_price,
          product_prices: u.product_prices ?? item.product_prices,
          ...(u.promotional_pack_unit_price !== undefined && {
            promotional_pack_unit_price: u.promotional_pack_unit_price,
          }),
        };
      });
    }
  }

  const stockRes = await cartStockUpdateService.checkStock(
    working.map((item) => ({
      id: item.id,
      sku: item.sku,
      name: item.name || String(item.sku),
      image: item.image,
      quantity: item.quantity ?? 1,
      stock: item.stock,
      pbm_flag: item?.pbm_info?.flag,
    })),
  );

  if (!stockRes.success || stockRes.changes.length === 0) {
    return working;
  }

  const soldOutIds = new Set(
    stockRes.changes.filter((c) => c.status === 'sold_out').map((c) => c.id),
  );
  const reducedById = new Map(
    stockRes.changes
      .filter((c) => c.status === 'reduced')
      .map((c) => [c.id, c.availableStock]),
  );

  return working
    .filter((item) => !soldOutIds.has(item.id))
    .map((item) => {
      const cap = reducedById.get(item.id);
      if (cap === undefined) return item;
      const q = Math.min(item.quantity ?? 1, cap);
      return {
        ...item,
        quantity: q,
        stock: cap,
      };
    });
}
